import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { summarizeComboCounts, pivotComboCounts } from '@hypo/shared'

// --- Styles ---

const editorLayout = css`
  display: flex;
  gap: ${space.lg};
`

const leftPanel = css`
  flex: 0 0 280px;
`

const rightPanel = css`
  flex: 1;
  min-width: 0;
`

const filterGroup = css`
  margin-bottom: ${space.md};
`

const filterLabel = css`
  display: block;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  margin-bottom: ${space.xs};
`

const checkboxList = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const checkboxItem = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  cursor: pointer;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  user-select: none;
  transition: background 0.1s, border-color 0.1s;
  &:hover { border-color: ${colors.brand}; }
`

const checkboxItemActive = css`
  ${checkboxItem};
  background: ${colors.brand};
  border-color: ${colors.brand};
  color: white;
`

const coverageStatsBox = css`
  margin-top: ${space.lg};
  padding: ${space.md};
  background: ${colors.bgSidebar};
  border-radius: 6px;
`

const coverageStatText = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
`

const progressBarOuter = css`
  width: 100%;
  height: 8px;
  background: ${colors.border};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${space.sm};
`

const progressBarFill = css`
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
`

const uncoveredToggle = css`
  font-size: ${font.size.xs};
  color: ${colors.brand};
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  text-decoration: underline;
  &:hover { color: ${colors.brandDark}; }
`

const uncoveredListStyle = css`
  margin-top: ${space.sm};
  max-height: 200px;
  overflow-y: auto;
`

const uncoveredComboItem = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px ${space.sm};
  cursor: pointer;
  border-radius: 4px;
  font-size: ${font.size.xs};
  margin-bottom: 2px;
  &:hover { background: ${colors.bgCard}; }
`

const analysisSectionStyle = css`
  margin-top: ${space.lg};
  padding: ${space.md};
  background: ${colors.bgSidebar};
  border-radius: 6px;
`

const analysisTitleStyle = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
`

const statRowStyle = css`
  display: flex;
  gap: ${space.md};
  margin-bottom: ${space.sm};
`

const statItemStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textSecondary};
  span { font-weight: ${font.weight.semibold}; color: ${colors.text}; }
`

const thinLinkStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.brand};
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  text-decoration: underline;
  &:hover { color: ${colors.brandDark}; }
`

const thinListStyle = css`
  margin-top: ${space.sm};
  max-height: 160px;
  overflow-y: auto;
`

const thinComboRow = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 3px ${space.sm};
  cursor: pointer;
  border-radius: 4px;
  font-size: ${font.size.xs};
  margin-bottom: 2px;
  &:hover { background: ${colors.bgCard}; }
`

const axisSelect = css`
  font-size: ${font.size.xs};
  padding: 2px 4px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  background: ${colors.bgCard};
  font-family: ${font.family};
`

const heatmapTable = css`
  border-collapse: collapse;
  width: 100%;
  margin-top: ${space.sm};
  font-size: ${font.size.xs};
`

const heatmapTh = css`
  padding: 4px 8px;
  font-weight: ${font.weight.semibold};
  color: ${colors.textSecondary};
  text-align: left;
  border-bottom: 1px solid ${colors.border};
  white-space: nowrap;
`

const heatmapTd = css`
  padding: 4px 6px;
  text-align: center;
  font-weight: ${font.weight.medium};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  white-space: nowrap;
  &:hover { outline: 2px solid ${colors.brand}; outline-offset: -2px; }
`

const heatmapRowHeader = css`
  padding: 4px 8px;
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  border-bottom: 1px solid ${colors.border};
  white-space: nowrap;
`

const heatmapSectionStyle = css`
  margin-top: ${space.lg};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  background: ${colors.bgCard};
`

const heatmapToggleStyle = css`
  display: flex;
  align-items: center;
  padding: ${space.sm} ${space.md};
  cursor: pointer;
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  user-select: none;
  &:hover { color: ${colors.text}; }
`

const criterionCard = css`
  ${card};
  padding: ${space.md};
  margin-bottom: ${space.sm};
`

const criterionHeader = css`
  display: flex;
  align-items: flex-start;
  gap: ${space.sm};
`

const criterionBody = css`
  flex: 1;
  min-width: 0;
`

const criterionDesc = css`
  font-size: ${font.size.sm};
  margin-bottom: ${space.xs};
`

const criterionMeta = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.sm};
  align-items: center;
`

const critBadge = css`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 3px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
`

const scopePill = css`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 12px;
  font-size: ${font.size.xs};
  background: ${colors.bgSidebar};
  border: 1px solid ${colors.border};
  color: ${colors.textSecondary};
`

const universalPill = css`
  ${scopePill};
  background: ${colors.bgSidebar};
  font-style: italic;
`

const criterionActions = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
`

const moveBtn = css`
  cursor: pointer;
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
  padding: 2px 6px;
  border-radius: 3px;
  border: none;
  background: none;
  &:hover { background: ${colors.bgSidebar}; color: ${colors.text}; }
`

const actionBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: 2px 8px;
`

const dangerBtn = css`
  ${actionBtn};
  color: ${colors.critical};
  border-color: ${colors.critical};
  &:hover { background: ${colors.brandLight}; }
`

const inlineEditArea = css`
  margin-top: ${space.sm};
  padding: ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  background: ${colors.bg};
`

const formRow = css`
  margin-bottom: ${space.sm};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.sm};
`

const addCriterionBtn = css`
  ${btnSecondary};
  font-size: ${font.size.sm};
  width: 100%;
  margin-top: ${space.sm};
`

const emptyState = css`
  text-align: center;
  padding: ${space.lg};
  color: ${colors.textMuted};
  font-size: ${font.size.sm};
`

const confirmOverlay = css`
  margin-top: ${space.sm};
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.orange};
  border-radius: 6px;
  background: #fff8f0;
  font-size: ${font.size.sm};
`

const confirmText = css`
  margin-bottom: ${space.sm};
  color: ${colors.textSecondary};
`

const confirmActions = css`
  display: flex;
  gap: ${space.sm};
`

const criticalitySelect = css`
  ${input};
  appearance: auto;
  font-size: ${font.size.xs};
  padding: 4px 8px;
  width: auto;
  display: inline-block;
`

// --- Helper Functions ---

function getDetailValues(detail) {
  if (detail.type === 'boolean') return [{ id: 'true', label: 'true' }, { id: 'false', label: 'false' }, { id: '_unknown', label: 'Unknown' }]
  if (detail.type === 'enum' && detail.options && detail.options.length > 0) {
    const vals = detail.options.map(o => typeof o === 'string' ? { id: o, label: o } : o)
    vals.push({ id: '_unknown', label: 'Unknown' })
    return vals
  }
  return null
}

function getValueLabel(details, detailId, value) {
  if (value === '_unknown') return 'Unknown'
  const d = details.find(det => det.id === detailId)
  if (!d || !d.options) return value
  const opt = d.options.find(o => (typeof o === 'string' ? o : o.id) === value)
  return opt ? (typeof opt === 'string' ? opt : (opt.label || opt.id)) : value
}

function getDetailName(details, detailId) {
  const d = details.find(det => det.id === detailId)
  return d ? d.name : detailId
}

function heatmapColor(value, max) {
  if (max === 0) return '#fce4ec'
  if (value === 0) return '#fce4ec' // red for uncovered
  const t = Math.min(value / max, 1)
  // Interpolate: red(0) → yellow(0.5) → green(1)
  if (t < 0.5) {
    const p = t * 2
    const r = Math.round(255 - p * 30)
    const g = Math.round(200 + p * 55)
    const b = Math.round(200 - p * 70)
    return `rgb(${r},${g},${b})`
  }
  const p = (t - 0.5) * 2
  const r = Math.round(225 - p * 45)
  const g = Math.round(255 - p * 10)
  const b = Math.round(130 + p * 50)
  return `rgb(${r},${g},${b})`
}

function criterionMatchesFilter(criterion, filter) {
  const scopes = criterion.scopes || []
  if (scopes.length === 0) return true // universal matches everything
  const groups = new Map()
  for (const s of scopes) {
    if (!groups.has(s.detail_id)) groups.set(s.detail_id, [])
    groups.get(s.detail_id).push(s.detail_value)
  }
  for (const [detailId, allowed] of groups) {
    const filterVals = filter[detailId]
    if (!filterVals || filterVals.size === 0) continue // "Any" — no constraint
    // Criterion must have at least one value that overlaps with the filter
    if (!allowed.some(v => filterVals.has(v))) return false
  }
  return true
}

function scopeIsBroaderThanFilter(criterion, filter) {
  const scopes = criterion.scopes || []
  if (scopes.length === 0) return true // universal
  const activeFilters = Object.entries(filter).filter(([, v]) => v && v.size > 0)
  if (activeFilters.length === 0) return false
  const groups = new Map()
  for (const s of scopes) {
    if (!groups.has(s.detail_id)) groups.set(s.detail_id, new Set())
    groups.get(s.detail_id).add(s.detail_value)
  }
  for (const [detailId, filterVals] of activeFilters) {
    const scopeVals = groups.get(detailId)
    if (!scopeVals) return true // scope doesn't constrain this dimension
    // Scope is broader if it allows values the filter doesn't
    for (const sv of scopeVals) {
      if (!filterVals.has(sv)) return true
    }
  }
  return false
}

function critBadgeStyle(criticality) {
  switch (criticality) {
    case 'critical': return `background: ${colors.critical}; color: white;`
    case 'important': return `background: ${colors.orange}; color: white;`
    case 'info': return `background: ${colors.border}; color: ${colors.text};`
    default: return ''
  }
}

function buildScopeFromFilter(filter) {
  const scope = []
  for (const [detailId, values] of Object.entries(filter)) {
    if (values && values.size > 0) {
      for (const v of values) {
        scope.push({ detailId, detailValue: v })
      }
    }
  }
  return scope
}

/**
 * CriteriaEditor component.
 *
 * attrs:
 *   criteria: array of { id, description, criticality, expected_answer, sort_order, scopes }
 *   details: array of pack_details with options
 *   coverage: { totalCombinations, coveredCombinations, percentage, uncoveredCombos }
 *   onCreateCriterion: (data) => Promise
 *   onUpdateCriterion: (criterionId, data) => Promise
 *   onDeleteCriterion: (criterionId) => Promise
 *   onSetScope: (criterionId, scope) => Promise
 *   onSplitCriterion: (criterionId, newScope) => Promise
 *   onReorder: (ids) => Promise
 *   saving: boolean
 */
export function CriteriaEditor() {
  let filter = {} // { detailId: Set<string> } — empty Set = "Any"
  let showUncovered = false
  let editingId = null
  let editDraft = null // { description, criticality, expected_answer }
  let addingNew = false
  let newDraft = null
  let splitConfirmId = null // criterionId awaiting split confirmation
  let editingScopeId = null // criterionId whose scope is being edited
  let scopeDraft = null // { detailId: Set<string> }
  let heatmapRow = null // detail ID for row axis
  let heatmapCol = null // detail ID for col axis
  let showThinList = false // expand the thin-coverage combo list
  let heatmapOpen = false // collapsible heatmap section

  function resetEdit() {
    editingId = null
    editDraft = null
    splitConfirmId = null
    editingScopeId = null
    scopeDraft = null
  }

  function startScopeEdit(criterion, details) {
    editingScopeId = criterion.id
    // Build draft from current scopes
    scopeDraft = {}
    for (const d of (details || [])) {
      if (getDetailValues(d)) scopeDraft[d.id] = new Set()
    }
    for (const s of (criterion.scopes || [])) {
      if (!scopeDraft[s.detail_id]) scopeDraft[s.detail_id] = new Set()
      scopeDraft[s.detail_id].add(s.detail_value)
    }
    // Close other edit modes
    editingId = null
    editDraft = null
    splitConfirmId = null
    addingNew = false
    newDraft = null
  }

  function cancelScopeEdit() {
    editingScopeId = null
    scopeDraft = null
  }

  function startEdit(criterion) {
    editingId = criterion.id
    editDraft = {
      description: criterion.description,
      criticality: criterion.criticality,
      expected_answer: criterion.expected_answer || '',
    }
    splitConfirmId = null
    addingNew = false
    newDraft = null
  }

  function startAdd() {
    addingNew = true
    newDraft = {
      description: '',
      criticality: 'important',
      expected_answer: '',
    }
    resetEdit()
  }

  function cancelAdd() {
    addingNew = false
    newDraft = null
  }

  function setFilterToCombo(combo) {
    // Reset all filters first, then set just the combo's values
    for (const key of Object.keys(filter)) {
      filter[key] = new Set()
    }
    for (const [detailId, value] of Object.entries(combo)) {
      if (!filter[detailId]) filter[detailId] = new Set()
      filter[detailId].add(value)
    }
    showUncovered = false
  }

  return {
    view(vnode) {
      const { criteria, details, coverage, onCreateCriterion, onUpdateCriterion,
              onDeleteCriterion, onSetScope, onSplitCriterion, onReorder, saving } = vnode.attrs

      // Ensure filter has entries for all details — default to _unknown
      for (const d of (details || [])) {
        if (!(d.id in filter)) filter[d.id] = new Set(['_unknown'])
      }

      // Filter and sort criteria
      const filtered = (criteria || []).filter(c => criterionMatchesFilter(c, filter))
      filtered.sort((a, b) => {
        const aUniversal = !a.scopes || a.scopes.length === 0
        const bUniversal = !b.scopes || b.scopes.length === 0
        if (aUniversal && !bUniversal) return -1
        if (!aUniversal && bUniversal) return 1
        return (a.sort_order || 0) - (b.sort_order || 0)
      })

      const hasComboCounts = coverage && coverage.comboCounts && coverage.comboCounts.length > 0
      const combinableDetails = (details || []).filter(d => getDetailValues(d))
      const showHeatmap = hasComboCounts && combinableDetails.length >= 2

      const children = []
      children.push(m('div', { class: editorLayout, key: 'editor' }, [
        m('div', { class: leftPanel, key: 'left-panel' }, renderLeftPanel(details, coverage)),
        m('div', { class: rightPanel, key: 'right-panel' },
          renderRightPanel(filtered, details, {
            onCreateCriterion, onUpdateCriterion, onDeleteCriterion,
            onSetScope, onSplitCriterion, onReorder, saving, criteria,
          })
        ),
      ]))

      if (showHeatmap) {
        children.push(renderHeatmapSection(details, coverage, combinableDetails))
      }

      return m('div', children)
    },
  }

  function renderLeftPanel(details, coverage) {
    const children = []

    // Filter checkboxes
    for (const d of (details || [])) {
      const vals = getDetailValues(d)
      if (!vals) continue
      const selected = filter[d.id] || new Set()

      const chips = []
      for (const v of vals) {
        const isActive = selected.has(v.id)
        chips.push(m('span', {
          key: v.id,
          class: isActive ? checkboxItemActive : checkboxItem,
          onclick: () => {
            if (!filter[d.id]) filter[d.id] = new Set()
            if (filter[d.id].has(v.id)) {
              filter[d.id].delete(v.id)
              // If nothing selected, default back to _unknown
              if (filter[d.id].size === 0) filter[d.id].add('_unknown')
            } else {
              // Selecting a specific value deselects _unknown (unless it IS _unknown)
              if (v.id !== '_unknown') filter[d.id].delete('_unknown')
              // Selecting _unknown deselects everything else
              if (v.id === '_unknown') filter[d.id].clear()
              filter[d.id].add(v.id)
            }
          },
        }, v.label))
      }

      children.push(m('div', { class: filterGroup, key: `filter-${d.id}` }, [
        m('label', { class: filterLabel }, d.name),
        m('div', { class: checkboxList }, chips),
      ]))
    }

    // Coverage: show unscoped detail values
    if (coverage) {
      const { unscopedValues, coveredCombinations } = coverage

      if (unscopedValues && unscopedValues.length > 0 && coveredCombinations > 0) {
        // Group by detail
        const byDetail = new Map()
        for (const { detailId, value } of unscopedValues) {
          if (!byDetail.has(detailId)) byDetail.set(detailId, [])
          byDetail.get(detailId).push(value)
        }

        const items = []
        for (const [detailId, values] of byDetail) {
          const chips = []
          for (const v of values) {
            chips.push(m('span', {
              key: v,
              style: `display: inline-block; padding: 1px 6px; border-radius: 3px; background: #fce4ec; color: ${colors.critical}; font-size: ${font.size.xs}; cursor: pointer;`,
              onclick: () => {
                for (const key of Object.keys(filter)) filter[key] = new Set(['_unknown'])
                if (!filter[detailId]) filter[detailId] = new Set()
                else filter[detailId].clear()
                filter[detailId].add(v)
              },
            }, getValueLabel(details || [], detailId, v)))
          }
          items.push(m('div', { key: `unscoped-${detailId}`, style: `margin-bottom: ${space.xs};` }, [
            m('div', {
              key: 'label',
              style: `font-size: ${font.size.xs}; font-weight: ${font.weight.medium}; color: ${colors.textSecondary}; margin-bottom: 2px;`,
            }, getDetailName(details || [], detailId)),
            m('div', { key: 'chips', style: `display: flex; flex-wrap: wrap; gap: 4px;` }, chips),
          ]))
        }

        children.push(m('div', { class: coverageStatsBox, key: 'coverage-stats' }, [
          m('div', {
            key: 'title',
            style: `font-size: ${font.size.xs}; font-weight: ${font.weight.semibold}; color: ${colors.orange}; margin-bottom: ${space.sm};`,
          }, 'No criteria scoped to:'),
          ...items,
        ]))
      } else if (coveredCombinations > 0) {
        children.push(m('div', { class: coverageStatsBox, key: 'coverage-stats' }, [
          m('div', {
            key: 'all-good',
            style: `font-size: ${font.size.xs}; color: ${colors.green}; font-weight: ${font.weight.medium};`,
          }, 'All detail values have criteria scoped to them.'),
        ]))
      }
    }

    // Coverage analysis: summary stats only (heatmap is full-width below)
    if (coverage && coverage.comboCounts && coverage.comboCounts.length > 0) {
      children.push(renderCoverageSummary(details, coverage))
    }

    return children
  }

  function renderCoverageSummary(details, coverage) {
    const { comboCounts } = coverage
    const summary = summarizeComboCounts(comboCounts)
    if (!summary) return null

    const sectionChildren = []

    sectionChildren.push(m('div', { key: 'title', class: analysisTitleStyle }, 'Coverage Analysis'))

    // Summary stats row
    const statsItems = []
    statsItems.push(m('div', { key: 'median', class: statItemStyle },
      ['Median: ', m('span', Math.round(summary.median * 10) / 10)]))
    statsItems.push(m('div', { key: 'mean', class: statItemStyle },
      ['Mean: ', m('span', Math.round(summary.mean * 10) / 10)]))
    sectionChildren.push(m('div', { key: 'stats', class: statRowStyle }, statsItems))

    // Thin/zero counts
    const warningItems = []
    if (summary.zeroCount > 0) {
      warningItems.push(m('span', {
        key: 'zero',
        style: `font-size: ${font.size.xs}; color: ${colors.critical}; font-weight: ${font.weight.medium};`,
      }, `${summary.zeroCount} uncovered`))
    }
    if (summary.thinCount > 0) {
      warningItems.push(m('button', {
        key: 'thin',
        class: thinLinkStyle,
        onclick: () => { showThinList = !showThinList },
      }, `${summary.thinCount} with only 1 criterion ${showThinList ? '\u25B4' : '\u25BE'}`))
    }
    if (warningItems.length > 0) {
      sectionChildren.push(m('div', {
        key: 'warnings',
        style: `display: flex; gap: ${space.md}; flex-wrap: wrap;`,
      }, warningItems))
    }

    // Expandable thin-coverage list
    if (showThinList && summary.thinCount > 0) {
      const thinCombos = comboCounts.filter(c => c.count === 1).slice(0, 30)
      const listItems = []
      for (let i = 0; i < thinCombos.length; i++) {
        const { combo } = thinCombos[i]
        const chips = []
        for (const [detailId, value] of Object.entries(combo)) {
          chips.push(m('span', {
            key: detailId,
            style: `display: inline-block; padding: 1px 6px; border-radius: 3px; background: ${colors.bgCard}; border: 1px solid ${colors.border}; font-size: ${font.size.xs};`,
          }, [
            `${getDetailName(details || [], detailId)}: `,
            m('strong', getValueLabel(details || [], detailId, value)),
          ]))
        }
        listItems.push(m('div', {
          key: `thin-${i}`,
          class: thinComboRow,
          onclick: () => setFilterToCombo(combo),
        }, chips))
      }
      if (comboCounts.filter(c => c.count === 1).length > 30) {
        listItems.push(m('div', {
          key: 'thin-more',
          style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; text-align: center; padding: ${space.xs};`,
        }, `\u2026 and ${comboCounts.filter(c => c.count === 1).length - 30} more`))
      }
      sectionChildren.push(m('div', { key: 'thin-list', class: thinListStyle }, listItems))
    }

    return m('div', { class: analysisSectionStyle, key: 'coverage-summary' }, sectionChildren)
  }

  function renderHeatmapSection(details, coverage, combinableDetails) {
    const { comboCounts } = coverage

    // Auto-default heatmap axes to first two combinable details
    if (!heatmapRow || !combinableDetails.find(d => d.id === heatmapRow)) heatmapRow = combinableDetails[0].id
    if (!heatmapCol || !combinableDetails.find(d => d.id === heatmapCol)) {
      heatmapCol = combinableDetails[1].id === heatmapRow ? combinableDetails[0].id : combinableDetails[1].id
    }
    if (heatmapRow === heatmapCol) {
      heatmapCol = combinableDetails.find(d => d.id !== heatmapRow).id
    }

    // Collapsed header
    const headerChildren = []
    headerChildren.push(m('span', { key: 'arrow', style: `margin-right: ${space.xs};` },
      heatmapOpen ? '\u25BE' : '\u25B8'))
    headerChildren.push(m('span', { key: 'text' }, 'Coverage Heatmap'))

    const sectionChildren = []
    sectionChildren.push(m('div', {
      key: 'header',
      class: heatmapToggleStyle,
      onclick: () => { heatmapOpen = !heatmapOpen },
    }, headerChildren))

    if (heatmapOpen) {
      const pivot = pivotComboCounts(comboCounts, heatmapRow, heatmapCol)

      // Axis selectors
      const axisRow = []
      axisRow.push(m('select', {
        key: 'row-sel',
        class: axisSelect,
        value: heatmapRow,
        onchange: (e) => { heatmapRow = e.target.value },
      }, combinableDetails.filter(d => d.id !== heatmapCol).map(d =>
        m('option', { value: d.id, key: d.id }, d.name)
      )))
      axisRow.push(m('span', {
        key: 'x',
        style: `font-size: ${font.size.xs}; color: ${colors.textMuted};`,
      }, '\u00d7'))
      axisRow.push(m('select', {
        key: 'col-sel',
        class: axisSelect,
        value: heatmapCol,
        onchange: (e) => { heatmapCol = e.target.value },
      }, combinableDetails.filter(d => d.id !== heatmapRow).map(d =>
        m('option', { value: d.id, key: d.id }, d.name)
      )))
      sectionChildren.push(m('div', {
        key: 'axes',
        style: `display: flex; gap: ${space.xs}; align-items: center; margin-top: ${space.sm}; padding: 0 ${space.md};`,
      }, axisRow))

      // Heatmap table
      if (pivot.rows.length > 0 && pivot.cols.length > 0) {
        const headerCells = [m('th', { key: 'corner', class: heatmapTh }, '')]
        for (const c of pivot.cols) {
          headerCells.push(m('th', {
            key: c,
            class: heatmapTh,
            title: getValueLabel(details || [], heatmapCol, c),
          }, getValueLabel(details || [], heatmapCol, c)))
        }

        const rows = []
        for (const r of pivot.rows) {
          const cells = []
          cells.push(m('td', {
            key: 'label',
            class: heatmapRowHeader,
            title: getValueLabel(details || [], heatmapRow, r),
          }, getValueLabel(details || [], heatmapRow, r)))
          for (const c of pivot.cols) {
            const cell = pivot.cells[r]?.[c]
            const minVal = cell ? cell.min : 0
            const bg = heatmapColor(minVal, pivot.maxMin)
            cells.push(m('td', {
              key: c,
              class: heatmapTd,
              style: `background: ${bg}; color: ${minVal === 0 ? colors.critical : colors.text};`,
              title: cell ? `Min: ${cell.min}, Avg: ${Math.round(cell.total / cell.n * 10) / 10}` : 'No combinations',
              onclick: () => {
                for (const key of Object.keys(filter)) filter[key] = new Set(['_unknown'])
                if (!filter[heatmapRow]) filter[heatmapRow] = new Set()
                else filter[heatmapRow].clear()
                filter[heatmapRow].add(r)
                if (!filter[heatmapCol]) filter[heatmapCol] = new Set()
                else filter[heatmapCol].clear()
                filter[heatmapCol].add(c)
              },
            }, minVal))
          }
          rows.push(m('tr', { key: r }, cells))
        }

        sectionChildren.push(m('div', {
          key: 'table-wrap',
          style: `overflow-x: auto; padding: 0 ${space.md} ${space.md};`,
        }, m('table', { class: heatmapTable }, [
          m('thead', { key: 'head' }, m('tr', { key: 'header-row' }, headerCells)),
          m('tbody', { key: 'body' }, rows),
        ])))

        sectionChildren.push(m('div', {
          key: 'legend',
          style: `font-size: 10px; color: ${colors.textMuted}; padding: 0 ${space.md} ${space.md}; font-style: italic;`,
        }, 'Min criteria per cell. Click to filter.'))
      }
    }

    return m('div', { class: heatmapSectionStyle, key: 'heatmap-section' }, sectionChildren)
  }

  function renderRightPanel(filtered, details, handlers) {
    const { onCreateCriterion, onUpdateCriterion, onDeleteCriterion,
            onSetScope, onSplitCriterion, onReorder, saving, criteria } = handlers

    const children = []

    if (filtered.length === 0 && !addingNew) {
      children.push(m('div', { class: emptyState, key: 'empty' },
        'No criteria match the current filter. Add one below.'))
    }

    for (let idx = 0; idx < filtered.length; idx++) {
      const c = filtered[idx]
      const isEditing = editingId === c.id
      const isSplitConfirm = splitConfirmId === c.id

      const cardChildren = []

      // Header row
      const headerItems = []

      // Reorder buttons
      headerItems.push(m('div', { class: criterionActions, key: 'actions' }, (() => {
        const btns = []
        if (idx > 0) {
          btns.push(m('button', {
            key: 'up',
            class: moveBtn,
            onclick: () => {
              const ids = filtered.map(cr => cr.id)
              const temp = ids[idx]
              ids[idx] = ids[idx - 1]
              ids[idx - 1] = temp
              onReorder(ids)
            },
          }, '\u25B2'))
        } else {
          btns.push(m('button', {
            key: 'up',
            class: moveBtn,
            style: 'visibility: hidden;',
          }, '\u25B2'))
        }
        if (idx < filtered.length - 1) {
          btns.push(m('button', {
            key: 'down',
            class: moveBtn,
            onclick: () => {
              const ids = filtered.map(cr => cr.id)
              const temp = ids[idx]
              ids[idx] = ids[idx + 1]
              ids[idx + 1] = temp
              onReorder(ids)
            },
          }, '\u25BC'))
        } else {
          btns.push(m('button', {
            key: 'down',
            class: moveBtn,
            style: 'visibility: hidden;',
          }, '\u25BC'))
        }
        return btns
      })()))

      // Body
      const bodyChildren = []

      bodyChildren.push(m('div', { key: 'desc', class: criterionDesc }, c.description))

      // Meta row: criticality badge, expected answer, scope pills
      const metaItems = []
      metaItems.push(m('span', {
        key: 'crit-badge',
        class: critBadge,
        style: critBadgeStyle(c.criticality),
      }, c.criticality))

      if (c.expected_answer) {
        metaItems.push(m('span', {
          key: 'expected',
          style: `font-size: ${font.size.xs}; color: ${colors.textMuted};`,
        }, `Expected: ${c.expected_answer}`))
      }

      bodyChildren.push(m('div', { key: 'meta', class: criterionMeta }, metaItems))

      // Scope pills (clickable to edit)
      const isEditingScope = editingScopeId === c.id
      const scopeItems = []
      const scopes = c.scopes || []
      if (scopes.length === 0) {
        scopeItems.push(m('span', {
          key: 'universal',
          class: universalPill,
          style: 'cursor: pointer;',
          onclick: () => startScopeEdit(c, details),
          title: 'Click to set scope',
        }, 'All combinations'))
      } else {
        for (const s of scopes) {
          scopeItems.push(m('span', {
            key: `${s.detail_id}-${s.detail_value}`,
            class: scopePill,
            style: 'cursor: pointer;',
            onclick: () => startScopeEdit(c, details),
            title: 'Click to edit scope',
          }, `${getDetailName(details || [], s.detail_id)}: ${getValueLabel(details || [], s.detail_id, s.detail_value)}`))
        }
      }
      bodyChildren.push(m('div', {
        key: 'scopes',
        style: `display: flex; flex-wrap: wrap; gap: 4px; margin-top: ${space.xs}; align-items: center;`,
      }, scopeItems))

      headerItems.push(m('div', { class: criterionBody, key: 'body' }, bodyChildren))

      // Edit/Delete buttons
      headerItems.push(m('div', {
        key: 'btns',
        style: `display: flex; flex-direction: column; gap: 4px; flex-shrink: 0;`,
      }, [
        m('button', {
          key: 'edit',
          class: actionBtn,
          onclick: () => {
            if (isEditing) {
              resetEdit()
            } else {
              // Check if scope is broader than filter
              if (scopeIsBroaderThanFilter(c, filter)) {
                // Show split confirmation
                splitConfirmId = c.id
                editingId = null
                editDraft = null
              } else {
                startEdit(c)
              }
            }
          },
        }, isEditing ? 'Cancel' : 'Edit'),
        m('button', {
          key: 'delete',
          class: dangerBtn,
          onclick: () => {
            onDeleteCriterion(c.id)
            if (editingId === c.id) resetEdit()
          },
        }, 'Delete'),
      ]))

      cardChildren.push(m('div', { key: 'header', class: criterionHeader }, headerItems))

      // Split confirmation
      if (isSplitConfirm) {
        const hasActiveFilter = Object.values(filter).some(v => v && v.size > 0)
        cardChildren.push(m('div', { key: 'split-confirm', class: confirmOverlay }, [
          m('div', { class: confirmText },
            'This criterion applies to other combinations too. Apply changes to all, or just for the current filter?'),
          m('div', { class: confirmActions }, (() => {
            const btns = []
            btns.push(m('button', {
              key: 'all',
              class: actionBtn,
              onclick: () => {
                startEdit(c)
              },
            }, 'Edit all'))
            if (hasActiveFilter) {
              btns.push(m('button', {
                key: 'just-this',
                class: btnPrimary,
                style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
                disabled: saving,
                onclick: async () => {
                  const newScope = buildScopeFromFilter(filter)
                  if (newScope.length === 0) {
                    startEdit(c)
                    return
                  }
                  const result = await onSplitCriterion(c.id, newScope)
                  if (result && result.id) {
                    editingId = result.id
                    editDraft = {
                      description: result.description || c.description,
                      criticality: result.criticality || c.criticality,
                      expected_answer: result.expected_answer || c.expected_answer || '',
                    }
                    splitConfirmId = null
                  } else {
                    splitConfirmId = null
                  }
                },
              }, 'Just this filter'))
            }
            btns.push(m('button', {
              key: 'cancel-split',
              class: actionBtn,
              onclick: () => { splitConfirmId = null },
            }, 'Cancel'))
            return btns
          })()),
        ]))
      }

      // Inline edit form
      if (isEditing && editDraft) {
        cardChildren.push(m('div', { key: 'edit-form', class: inlineEditArea }, [
          m('div', { class: formRow }, [
            m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Description'),
            m('textarea', {
              class: input,
              rows: 3,
              value: editDraft.description,
              oninput: (e) => { editDraft.description = e.target.value },
              placeholder: 'Describe what should be evaluated...',
              style: `font-size: ${font.size.sm};`,
            }),
          ]),
          m('div', { style: `display: flex; gap: ${space.sm};` }, [
            m('div', { class: formRow, style: 'flex: 0 0 auto;' }, [
              m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Criticality'),
              m('select', {
                class: criticalitySelect,
                value: editDraft.criticality,
                onchange: (e) => { editDraft.criticality = e.target.value },
              }, [
                m('option', { value: 'critical', key: 'critical' }, 'Critical'),
                m('option', { value: 'important', key: 'important' }, 'Important'),
                m('option', { value: 'info', key: 'info' }, 'Info'),
              ]),
            ]),
            m('div', { class: formRow, style: 'flex: 1;' }, [
              m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Expected Answer'),
              m('input', {
                class: input,
                value: editDraft.expected_answer,
                oninput: (e) => { editDraft.expected_answer = e.target.value },
                placeholder: 'Expected answer (optional)',
                style: `font-size: ${font.size.xs}; padding: 4px 8px;`,
              }),
            ]),
          ]),
          m('div', { class: formActions }, [
            m('button', {
              key: 'cancel-edit',
              class: btnSecondary,
              style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
              onclick: resetEdit,
            }, 'Cancel'),
            m('button', {
              key: 'save-edit',
              class: btnPrimary,
              style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
              disabled: saving || !editDraft.description.trim(),
              onclick: async () => {
                await onUpdateCriterion(c.id, {
                  description: editDraft.description.trim(),
                  criticality: editDraft.criticality,
                  expected_answer: editDraft.expected_answer.trim() || null,
                })
                resetEdit()
              },
            }, saving ? 'Saving...' : 'Save'),
          ]),
        ]))
      }

      // Inline scope editor
      if (isEditingScope && scopeDraft) {
        const scopeEditorChildren = []
        for (const d of (details || [])) {
          const vals = getDetailValues(d)
          if (!vals) continue
          const selected = scopeDraft[d.id] || new Set()
          const chips = []
          for (const v of vals) {
            const isActive = selected.has(v.id)
            chips.push(m('span', {
              key: v.id,
              class: isActive ? checkboxItemActive : checkboxItem,
              onclick: () => {
                if (!scopeDraft[d.id]) scopeDraft[d.id] = new Set()
                if (scopeDraft[d.id].has(v.id)) {
                  scopeDraft[d.id].delete(v.id)
                } else {
                  scopeDraft[d.id].add(v.id)
                }
              },
            }, v.label))
          }
          scopeEditorChildren.push(m('div', { key: `scope-edit-${d.id}`, class: filterGroup }, [
            m('label', { class: filterLabel }, d.name),
            m('div', { class: checkboxList }, chips),
          ]))
        }
        scopeEditorChildren.push(m('div', {
          key: 'scope-hint',
          style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; font-style: italic; margin-bottom: ${space.sm};`,
        }, 'No selections = applies to all combinations'))
        scopeEditorChildren.push(m('div', { key: 'scope-actions', class: formActions }, [
          m('button', {
            key: 'cancel-scope',
            class: btnSecondary,
            style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
            onclick: cancelScopeEdit,
          }, 'Cancel'),
          m('button', {
            key: 'save-scope',
            class: btnPrimary,
            style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
            disabled: saving,
            onclick: async () => {
              const scope = []
              for (const [detailId, values] of Object.entries(scopeDraft)) {
                for (const v of values) {
                  scope.push({ detailId, detailValue: v })
                }
              }
              await onSetScope(c.id, scope)
              cancelScopeEdit()
            },
          }, saving ? 'Saving...' : 'Save Scope'),
        ]))
        cardChildren.push(m('div', { key: 'scope-editor', class: inlineEditArea }, scopeEditorChildren))
      }

      children.push(m('div', { class: criterionCard, key: c.id }, cardChildren))
    }

    // Add new criterion form
    if (addingNew && newDraft) {
      children.push(m('div', { class: criterionCard, key: 'new-criterion' }, [
        m('div', { key: 'new-form', class: inlineEditArea, style: 'margin-top: 0;' }, [
          m('div', { class: formRow }, [
            m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Description'),
            m('textarea', {
              class: input,
              rows: 3,
              value: newDraft.description,
              oninput: (e) => { newDraft.description = e.target.value },
              placeholder: 'Describe what should be evaluated...',
              style: `font-size: ${font.size.sm};`,
            }),
          ]),
          m('div', { style: `display: flex; gap: ${space.sm};` }, [
            m('div', { class: formRow, style: 'flex: 0 0 auto;' }, [
              m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Criticality'),
              m('select', {
                class: criticalitySelect,
                value: newDraft.criticality,
                onchange: (e) => { newDraft.criticality = e.target.value },
              }, [
                m('option', { value: 'critical', key: 'critical' }, 'Critical'),
                m('option', { value: 'important', key: 'important' }, 'Important'),
                m('option', { value: 'info', key: 'info' }, 'Info'),
              ]),
            ]),
            m('div', { class: formRow, style: 'flex: 1;' }, [
              m('label', { class: label, style: `font-size: ${font.size.xs};` }, 'Expected Answer'),
              m('input', {
                class: input,
                value: newDraft.expected_answer,
                oninput: (e) => { newDraft.expected_answer = e.target.value },
                placeholder: 'Expected answer (optional)',
                style: `font-size: ${font.size.xs}; padding: 4px 8px;`,
              }),
            ]),
          ]),

          // Show scope info
          (() => {
            const scope = buildScopeFromFilter(filter)
            if (scope.length > 0) {
              const scopeChips = []
              for (const s of scope) {
                scopeChips.push(m('span', {
                  key: `${s.detailId}-${s.detailValue}`,
                  class: scopePill,
                }, `${getDetailName(details || [], s.detailId)}: ${getValueLabel(details || [], s.detailId, s.detailValue)}`))
              }
              return m('div', {
                style: `margin-top: ${space.sm}; display: flex; flex-wrap: wrap; gap: 4px; align-items: center;`,
              }, [
                m('span', {
                  key: 'scope-label',
                  style: `font-size: ${font.size.xs}; color: ${colors.textMuted};`,
                }, 'Scope: '),
                ...scopeChips,
              ])
            }
            return m('div', {
              style: `margin-top: ${space.sm}; font-size: ${font.size.xs}; color: ${colors.textMuted}; font-style: italic;`,
            }, 'Universal (applies to all combinations)')
          })(),

          m('div', { class: formActions }, [
            m('button', {
              key: 'cancel-new',
              class: btnSecondary,
              style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
              onclick: cancelAdd,
            }, 'Cancel'),
            m('button', {
              key: 'save-new',
              class: btnPrimary,
              style: `font-size: ${font.size.xs}; padding: 2px 8px;`,
              disabled: saving || !newDraft.description.trim(),
              onclick: async () => {
                const scope = buildScopeFromFilter(filter)
                await onCreateCriterion({
                  description: newDraft.description.trim(),
                  criticality: newDraft.criticality,
                  expected_answer: newDraft.expected_answer.trim() || null,
                  scope,
                })
                cancelAdd()
              },
            }, saving ? 'Saving...' : 'Create'),
          ]),
        ]),
      ]))
    }

    // Add criterion button
    if (!addingNew) {
      children.push(m('button', {
        key: 'add-btn',
        class: addCriterionBtn,
        onclick: startAdd,
      }, '+ Add Criterion'))
    }

    return children
  }
}
